import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormModal } from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { toApiDate } from "@marekl/adminer/datetime";
import {
  useAddTeamMember,
  type AddMemberPayload,
} from "../../../lib/queries/memberships";
import {
  defaultMemberValues,
  memberSchema,
  type MemberFormOutput,
  type MemberFormValues,
} from "./memberFormSchema";
import { MemberFormFields } from "./MemberFormFields";
import type { ID } from "../../../lib/id";

type MemberKind = MemberFormValues["membership_kind"];

/** What this dialog is called, and what it asks for, per kind. */
const ADD_TITLES: Record<MemberKind, string> = {
  registered: "Přidat člena",
  guest: "Přidat neregistrovaného člena",
  foreign: "Přidat zahraničního člena",
};

/**
 * AddMemberModal creates ONE kind, decided by the caller, and never asks
 * which: two things already decide it and neither is the user's to pick.
 *
 * The team's federation decides first (membership.kindAllowedOn): a club
 * outside ČNSMS holds „Zahraniční členy" and nothing else. Then the club's own
 * season decides: a ČNSMS club that has not registered into the season cannot
 * hold a „Člen" at all (ErrTeamNotRegistered), so its roster dialog creates
 * „Nečleny" for pohárové soutěže. Registering the club is one deliberate act
 * on „Registrace do sezóny", with a fee and MČR eligibility attached, and it
 * must not happen because somebody filled in a roster form.
 *
 * That is also why the fields differ: only a „Člen" is asked for a rodné
 * číslo and an address (MemberFormFields), because only a registration needs
 * them.
 */
export function AddMemberModal({
  teamId,
  kind,
  open,
  onClose,
}: {
  teamId: ID;
  /** Which kind this dialog creates; see ADD_TITLES. */
  kind: MemberKind;
  open: boolean;
  onClose: () => void;
}) {
  const add = useAddTeamMember(teamId);
  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<MemberFormValues, unknown, MemberFormOutput>({
    resolver: zodResolver(memberSchema) as Resolver<MemberFormValues, unknown, MemberFormOutput>,
    defaultValues: { ...defaultMemberValues, membership_kind: kind },
    mode: "onTouched",
  });

  async function onSubmit(values: MemberFormOutput) {
    const payload: AddMemberPayload = {
      person: {
        ...values.person,
        birth_date: toApiDate(values.person.birth_date) ?? "",
      },
      roles: values.roles,
      kind: values.membership_kind,
    };
    try {
      await add.mutateAsync(payload);
      reset({ ...defaultMemberValues, membership_kind: kind });
      onClose();
    } catch (err) {
      apiErrorToFormErrors<MemberFormValues>(err, setError, {
        fieldMap: (field) => (field === "roles" ? "roles" : `person.${field}`) as never,
        fallbackMessage: "Člena se nepodařilo uložit.",
      });
    }
  }

  const rootMessage = rootErrorMessage(errors);

  return (
    <FormModal
      open={open}
      title={ADD_TITLES[kind]}
      submitLabel="Uložit"
      busy={add.isPending}
      error={rootMessage}
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
      width="lg"
    >
      {/* The kind is not asked for but it is known, and the fields read it
          to decide which of them are shown at all: without it every add form
          claimed the ČNSMS data set, „Přidat neregistrovaného člena" and
          „Přidat zahraničního člena" included. */}
      <MemberFormFields
        register={register}
        control={control}
        errors={errors}
        kind={kind}
        hideKind
      />
    </FormModal>
  );
}
